// File: LicenseIdUpdateApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function LicenseIdUpdateApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Reasons
    reasonChangeAddress: false,
    reasonNameChange: false,
    reasonRealID: false,
    reasonDuplicateReplacement: false, // lost/stolen/damaged

    // Personal
    legalFirst: '',
    legalMiddle: '',
    legalLast: '',
    dob: '',
    licenseIdNumber: '',
    ssnLast4: '',
    email: '',
    mobile: '',
    allowText: true,

    // Name change details (if checked)
    newFirst: '',
    newMiddle: '',
    newLast: '',
    nameChangeDocType: '', // Marriage Certificate | Court Order | Divorce Decree | Other
    nameChangeDocDate: '',

    // Residential address
    resStreet: '',
    resApt: '',
    resCity: '',
    resState: '',
    resZip: '',

    // Mailing
    mailingSameAsResidential: true,
    mailStreet: '',
    mailApt: '',
    mailCity: '',
    mailState: '',
    mailZip: '',

    // Real ID checklist
    ridIdentityDoc: false,
    ridSSNDoc: false,
    ridResidencyDoc1: false,
    ridResidencyDoc2: false,

    // Other options
    organDonor: false,
    veteranDesignation: false,

    // Voter registration address update
    voterAddressUpdate: '', // Yes | No

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  // helpers
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'mailingSameAsResidential') {
      const next = checked;
      setForm((f) => {
        const updated = { ...f, mailingSameAsResidential: next };
        if (next) {
          updated.mailStreet = f.resStreet;
          updated.mailApt = f.resApt;
          updated.mailCity = f.resCity;
          updated.mailState = f.resState.toUpperCase();
          updated.mailZip = f.resZip;
        }
        return updated;
      });
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    // Only require the first field (legalFirst) for demo purposes
    if (!String(form.legalFirst || '').trim()) {
      e.legalFirst = 'Required';
    }

    // Keep format validation for fields that are filled in (but don't require them)
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    if (form.mobile && !isTenPhone(form.mobile)) e.mobile = '10 digits';

    if (form.resState && !isTwoLetterState(form.resState)) e.resState = 'Use 2-letter code';
    if (form.resZip && !isFiveZip(form.resZip)) e.resZip = '5-digit ZIP';

    if (!form.mailingSameAsResidential) {
      if (form.mailState && !isTwoLetterState(form.mailState)) e.mailState = 'Use 2-letter code';
      if (form.mailZip && !isFiveZip(form.mailZip)) e.mailZip = '5-digit ZIP';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true); // success screen (demo)
    }, 500);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header / Actions */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">License / ID Update (DMV)</h4>
              <p className="gts-form-overview">
                Update your License or State ID (address or name changes, Real ID upgrade, replacement). This is a{' '}
                <strong>demo-only</strong> submission—no data is stored or transmitted.
              </p>
              <div className="gts-actions">
                <Link className="btn btn-secondary gts-toplink" to="/department/dmv">
                  DMV Info
                </Link>
              </div>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions">
                  <button className="btn btn-secondary" onClick={() => window.print()}>Print this page</button>
                  <Link to="/forms" className="btn btn-secondary">Back to Forms &amp; Applications</Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Reasons */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Reason for Update</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="reasonChangeAddress" checked={form.reasonChangeAddress} onChange={onChange} />
                      <span>Address change</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="reasonNameChange" checked={form.reasonNameChange} onChange={onChange} />
                      <span>Legal name change</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="reasonRealID" checked={form.reasonRealID} onChange={onChange} />
                      <span>Upgrade to Real ID</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input
                        type="checkbox"
                        name="reasonDuplicateReplacement"
                        checked={form.reasonDuplicateReplacement}
                        onChange={onChange}
                      />
                      <span>Replacement / duplicate (lost, stolen, or damaged)</span>
                    </label>
                    {errors.reasons && <em className="gts-error">{errors.reasons}</em>}
                  </div>
                </div>

                {/* Personal */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Personal Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Legal First <span className="gts-required">*</span></span><input name="legalFirst" value={form.legalFirst} onChange={onChange} />{errors.legalFirst && <em className="gts-error">{errors.legalFirst}</em>}</label>
                    <label className="gts-field"><span>Middle</span><input name="legalMiddle" value={form.legalMiddle} onChange={onChange} /></label>
                    <label className="gts-field"><span>Legal Last <span className="gts-required">*</span></span><input name="legalLast" value={form.legalLast} onChange={onChange} />{errors.legalLast && <em className="gts-error">{errors.legalLast}</em>}</label>
                    <label className="gts-field"><span>Date of Birth <span className="gts-required">*</span></span><input type="date" name="dob" value={form.dob} onChange={onChange} />{errors.dob && <em className="gts-error">{errors.dob}</em>}</label>
                    <label className="gts-field"><span>License/ID # <span className="gts-required">*</span></span><input name="licenseIdNumber" value={form.licenseIdNumber} onChange={onChange} />{errors.licenseIdNumber && <em className="gts-error">{errors.licenseIdNumber}</em>}</label>
                    <label className="gts-field"><span>SSN (last 4, optional)</span><input name="ssnLast4" maxLength={4} value={form.ssnLast4} onChange={onChange} /></label>

                    <label className="gts-field"><span>Email <span className="gts-required">*</span></span><input type="email" name="email" value={form.email} onChange={onChange} />{errors.email && <em className="gts-error">{errors.email}</em>}</label>
                    <label className="gts-field"><span>Mobile</span><input name="mobile" value={form.mobile} onChange={onChange} maxLength={14} />{errors.mobile && <em className="gts-error">{errors.mobile}</em>}</label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="allowText" checked={form.allowText} onChange={onChange} />
                      <span>Okay to text me about appointment updates</span>
                    </label>
                  </div>
                </div>

                {/* Name change details */}
                {form.reasonNameChange && (
                  <div className="gts-form-section">
                    <div className="gts-form-section-head"><h5>New Legal Name</h5></div>
                    <div className="gts-form-grid">
                      <label className="gts-field"><span>New First <span className="gts-required">*</span></span><input name="newFirst" value={form.newFirst} onChange={onChange} /></label>
                      <label className="gts-field"><span>New Middle</span><input name="newMiddle" value={form.newMiddle} onChange={onChange} /></label>
                      <label className="gts-field"><span>New Last <span className="gts-required">*</span></span><input name="newLast" value={form.newLast} onChange={onChange} /></label>
                      <label className="gts-field">
                        <span>Document Type <span className="gts-required">*</span></span>
                        <select name="nameChangeDocType" value={form.nameChangeDocType} onChange={onChange}>
                          <option value="">Select…</option>
                          <option>Marriage Certificate</option>
                          <option>Court Order</option>
                          <option>Divorce Decree</option>
                          <option>Other</option>
                        </select>
                        {errors.nameChangeDocType && <em className="gts-error">{errors.nameChangeDocType}</em>}
                      </label>
                      <label className="gts-field"><span>Document Date <span className="gts-required">*</span></span><input type="date" name="nameChangeDocDate" value={form.nameChangeDocDate} onChange={onChange} />{errors.nameChangeDocDate && <em className="gts-error">{errors.nameChangeDocDate}</em>}</label>
                      {errors.nameChange && <em className="gts-error">{errors.nameChange}</em>}
                    </div>
                  </div>
                )}

                {/* Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Residential Address</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2"><span>Street <span className="gts-required">*</span></span><input name="resStreet" value={form.resStreet} onChange={onChange} /></label>
                    <label className="gts-field"><span>Apt/Suite</span><input name="resApt" value={form.resApt} onChange={onChange} /></label>
                    <label className="gts-field"><span>City <span className="gts-required">*</span></span><input name="resCity" value={form.resCity} onChange={onChange} /></label>
                    <label className="gts-field"><span>State (2) <span className="gts-required">*</span></span><input name="resState" value={form.resState} onChange={onChange} maxLength={2} placeholder="TN" />{errors.resState && <em className="gts-error">{errors.resState}</em>}</label>
                    <label className="gts-field"><span>ZIP (5) <span className="gts-required">*</span></span><input name="resZip" value={form.resZip} onChange={onChange} maxLength={5} />{errors.resZip && <em className="gts-error">{errors.resZip}</em>}</label>
                    {errors.resAddress && <em className="gts-error">{errors.resAddress}</em>}

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="mailingSameAsResidential" checked={form.mailingSameAsResidential} onChange={onChange} />
                      <span>Mailing address is the same as residential</span>
                    </label>

                    {!form.mailingSameAsResidential && (
                      <>
                        <label className="gts-field gts-span-2"><span>Mailing Street</span><input name="mailStreet" value={form.mailStreet} onChange={onChange} /></label>
                        <label className="gts-field"><span>Apt/Suite</span><input name="mailApt" value={form.mailApt} onChange={onChange} /></label>
                        <label className="gts-field"><span>City</span><input name="mailCity" value={form.mailCity} onChange={onChange} /></label>
                        <label className="gts-field"><span>State (2)</span><input name="mailState" value={form.mailState} onChange={onChange} maxLength={2} /></label>
                        <label className="gts-field"><span>ZIP (5)</span><input name="mailZip" value={form.mailZip} onChange={onChange} maxLength={5} /></label>
                        {errors.mailState && <em className="gts-error">{errors.mailState}</em>}
                        {errors.mailZip && <em className="gts-error">{errors.mailZip}</em>}
                      </>
                    )}
                  </div>
                </div>

                {/* Real ID Checklist */}
                {form.reasonRealID && (
                  <div className="gts-form-section">
                    <div className="gts-form-section-head"><h5>Real ID Document Checklist</h5></div>
                    <div className="gts-panel gts-panel-yellow">
                      <div className="gts-panel-title">You’ll need to present the following at your visit:</div>
                      <ul className="gts-panel-list">
                        <li>One proof of identity (e.g., valid passport or certified birth certificate).</li>
                        <li>One proof of Social Security number (e.g., SSN card, W-2, 1099 with full SSN).</li>
                        <li>Two separate proofs of GTS residency (e.g., utility bill, bank statement, lease, paystub).</li>
                      </ul>
                      <div className="gts-checklist" style={{ marginTop: 8 }}>
                        <label className="gts-field gts-span-2 gts-checkboxline">
                          <input type="checkbox" name="ridIdentityDoc" checked={form.ridIdentityDoc} onChange={onChange} />
                          <span>I will bring an identity document</span>
                        </label>
                        <label className="gts-field gts-span-2 gts-checkboxline">
                          <input type="checkbox" name="ridSSNDoc" checked={form.ridSSNDoc} onChange={onChange} />
                          <span>I will bring SSN documentation</span>
                        </label>
                        <label className="gts-field gts-span-2 gts-checkboxline">
                          <input type="checkbox" name="ridResidencyDoc1" checked={form.ridResidencyDoc1} onChange={onChange} />
                          <span>I will bring proof of GTS residency (document #1)</span>
                        </label>
                        <label className="gts-field gts-span-2 gts-checkboxline">
                          <input type="checkbox" name="ridResidencyDoc2" checked={form.ridResidencyDoc2} onChange={onChange} />
                          <span>I will bring proof of GTS residency (document #2)</span>
                        </label>
                        {(errors.ridIdentityDoc || errors.ridSSNDoc || errors.ridResidency) && (
                          <em className="gts-error">
                            {errors.ridIdentityDoc || errors.ridSSNDoc || errors.ridResidency}
                          </em>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Options */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Additional Options</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="organDonor" checked={form.organDonor} onChange={onChange} />
                      <span>Add/update organ donor designation</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="veteranDesignation" checked={form.veteranDesignation} onChange={onChange} />
                      <span>Add veteran designation (documentation required in person)</span>
                    </label>
                  </div>
                </div>

                {/* Voter Registration Address Update */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Voter Registration Address Update</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Update voter registration address with this request?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((opt) => (
                          <label className="gts-radio" key={opt}>
                            <input type="radio" name="voterAddressUpdate" value={opt} checked={form.voterAddressUpdate===opt} onChange={onChange} />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Declaration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange} />
                      <span>
                        I certify the information provided is true and complete, and I understand this is a demo (no data is transmitted).
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange} />
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange} />
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions (kept off the signature box) */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: 5 }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LicenseIdUpdateApplication;
